import { useContext, useMemo } from 'react';
import { UserContext } from '@/contexts/UserContext';

/**
 * True when the signed-in user is a corporate admin sitting at org level ("All Accounts") for an
 * organization that owns no SHIPPER accounts — a carrier-only org such as WPL.
 *
 * WHY THIS EXISTS
 * carrier_invoices.organization_id is always derived from the SHIPPER's org, never the carrier's
 * (onward-core/src/accounting.js:81 -> :261 / :285). The ADMIN branches of the accounting views
 * filter on that stored column, so an org whose accounts are only ever the CARRIER side of an
 * order can never match: the stamp holds whichever org the shipper belongs to, or null. Those
 * views return zero rows for such an org no matter how the data is backfilled, because there is
 * no correct value to backfill to.
 *
 * Consumers use this to swap in a payee-scoped predicate (client_id in the org's own accounts)
 * that aggregates exactly what each account sees when drilled into individually.
 *
 * Detected rather than configured: `admin_clients` already carries `user.roles` per account, so
 * no schema change is needed. Trade-off accepted deliberately — if a SHIPPER is ever added to the
 * org, this goes false and the org reverts to the existing organization_id behaviour. That fails
 * toward today's behaviour rather than widening access, but it is a silent flip. Promote to an
 * explicit organizations-level flag if that becomes a problem.
 *
 * Requires `!clientId`: once an account is selected the per-account views already scope correctly,
 * and those branches must keep running untouched.
 */
export const useCarrierOrgAggregate = () => {
    const { user, clientId, admin_clients } = useContext(UserContext);

    return useMemo(() => {
        if (clientId) return false;
        if (!user?.roles?.['ADMIN']) return false;
        // An empty list means admin_clients has not resolved yet. Returning false keeps the
        // existing branches in play rather than briefly aggregating over nothing.
        if (!admin_clients?.length) return false;
        return !admin_clients.some((client) => client?.user?.roles?.['SHIPPER']);
    }, [user, clientId, admin_clients]);
};

/**
 * The org's own account ids — the payee side of every invoice the org can legitimately see.
 */
export const carrierOrgClientIds = (admin_clients) =>
    (admin_clients || []).map((client) => client?.client_id).filter(Boolean);
